import { FieldProps } from "@rjsf/utils";
import { useRef, useState } from "react";
import { MapLayerMouseEvent, useMap } from "react-map-gl/maplibre";

import { XYLocation } from "@nens/deck-gl";

import { getRanaWidgetFromProperty } from "@/features/project-process-run-form";

import { DrawGeometryFormData, DrawGeometryType } from "../types/draw-geometry-form-data";
import { getAcceptedGeometryTypes } from "../utils/get-accepted-geometry-types";

import { useGeometryPlaceName } from "./use-geometry-place-name";
import { useDrawLine } from "./use-map-draw-line";
import { useDrawPoint } from "./use-map-draw-point";
import { useDrawPolygon } from "./use-map-draw-polygon";

export type RemovePopup = { coordinates: XYLocation; onConfirm: () => void; type: string };

const ALL_MODES = ["point", "line", "polygon"] as const;
export type Mode = (typeof ALL_MODES)[number];
const modeToTypes: Record<Mode, DrawGeometryType[]> = {
  point: ["Point", "MultiPoint"],
  line: ["LineString", "MultiLineString"],
  polygon: ["Polygon", "MultiPolygon"],
};

export function useProcessRunFormDrawGeometry(props: FieldProps<DrawGeometryFormData>) {
  const map = useMap()[props.name];
  const { onChange, disabled, readonly, formData, schema } = props;
  const isDisabled = disabled || readonly;
  const widget = getRanaWidgetFromProperty(schema, "draw_geometry");
  if (!widget) throw new Error("Draw Geometry widget configuration not found in schema");
  const maxAreaSquareKm = widget.max_area_square_km;

  const acceptedGeometryTypes = getAcceptedGeometryTypes(schema);

  const modes = ALL_MODES.filter((mode) =>
    modeToTypes[mode].some((type) => acceptedGeometryTypes.includes(type))
  );

  const placeName = useGeometryPlaceName(formData);

  // Update placeName when it changes
  if (!isDisabled && placeName && formData && formData.properties?.placeName !== placeName) {
    onChange({
      ...formData,
      properties: { ...formData.properties, placeName },
    });
  }

  const [removePopup, setRemovePopup] = useState<RemovePopup | null>(null);
  const [mode, setMode] = useState<Mode>(modes[0]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [mouseLocation, setMouseLocation] = useState<XYLocation>([0, 0]);

  const modeAllowsMultiple = modeToTypes[mode].every((type) =>
    acceptedGeometryTypes.includes(type)
  );

  const getSubHookProps = (type: Mode) => ({
    active: mode === type,
    formData: formData ?? { type: modeToTypes[mode][0], coordinates: [] },
    onChange,
    isDeleting,
    acceptedGeometryTypes,
    mouseLocation,
    setRemovePopup,
    map,
    maxAreaSquareKm,
  });

  const {
    polygonLayer,
    polygons,
    onMapClick: onMapClickPolygon,
  } = useDrawPolygon(getSubHookProps("polygon"));

  const {
    pointLayer,
    points,
    onMapClick: onMapClickPoint,
  } = useDrawPoint(getSubHookProps("point"));

  const { lineLayer, lines, onMapClick: onMapClickLine } = useDrawLine(getSubHookProps("line"));

  const layers = [polygonLayer, pointLayer, lineLayer];
  const somethingOnMap = !!polygons.length || !!points.length || !!lines.length;

  if (!somethingOnMap && isDeleting) setIsDeleting(false);

  const changeMode = (newMode: Mode) => {
    setMode(newMode);
    if (mode !== newMode) {
      onChange({ type: modeToTypes[newMode][0], coordinates: [] });
      setIsDeleting(false);
    }
  };

  const onMapClick = (e: MapLayerMouseEvent) => {
    if (isDisabled) return;
    setMouseLocation(e.lngLat.toArray());
    onMapClickPolygon(e);
    onMapClickPoint(e);
    onMapClickLine(e);
  };

  const dontCallRef = useRef(0);
  const onMapMouseMove = (e: MapLayerMouseEvent) => {
    if (isDisabled) return;
    // Handle mouse move gets called way too often so this slows it down a bit
    if (dontCallRef.current) return dontCallRef.current--;
    dontCallRef.current += 2;
    setMouseLocation(e.lngLat.toArray());
  };

  const getMapCursor = ({ isHovering }: { isHovering: boolean }) => {
    if (isDisabled) return "";
    const getCursorStyle = () => {
      if (isHovering) return "pointer";
      if (!isDeleting && mode) return "crosshair";
      return "";
    };
    const cursorStyle = getCursorStyle();
    if (map) map.getCanvas().style.cursor = cursorStyle;
    return cursorStyle;
  };

  return {
    mode,
    modes,
    modeAllowsMultiple,
    changeMode,
    onMapClick,
    onMapMouseMove,
    getMapCursor,
    layers,
    removePopup,
    setRemovePopup,
    isDeleting,
    setIsDeleting,
    somethingOnMap,
  };
}
